import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { CalendarBlank } from "@phosphor-icons/react";
import { format } from "date-fns";
import { PRESETS, WEEKDAYS, addDays, presetToRange } from "./shared";

export const RangePicker = ({ value, onChange, isSingleDay = false, testIdPrefix = "rp" }) => {
  const set = (patch) => onChange({ ...value, ...patch });
  const setPreset = (p) => {
    if (p === "custom") {
      set({ preset: p });
      return;
    }
    const r = presetToRange(p);
    set({ preset: p, start: r.start, end: r.end });
  };
  const toggleDay = (idx) => {
    const cur = new Set(value.weekdays || []);
    if (cur.has(idx)) cur.delete(idx);
    else cur.add(idx);
    set({ weekdays: Array.from(cur) });
  };
  const allDays =
    !value.weekdays || value.weekdays.length === 0 || value.weekdays.length === 7;

  return (
    <div
      className="rounded-lg border border-border bg-secondary/20 p-3 space-y-3"
      data-testid={`${testIdPrefix}-root`}
    >
      <div className="flex flex-wrap items-center gap-2">
        {Object.entries(PRESETS)
          .filter(([k]) => (isSingleDay ? k !== "weekly" && k !== "monthly" : true))
          .map(([k, v]) => (
            <button
              key={k}
              type="button"
              onClick={() => setPreset(k)}
              className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${
                value.preset === k
                  ? "bg-card border-primary font-medium"
                  : "border-border bg-card/50 text-muted-foreground hover:border-muted-foreground/40"
              }`}
              data-testid={`${testIdPrefix}-preset-${k}`}
            >
              {isSingleDay && k === "daily" ? "Today" : v.label}
            </button>
          ))}
      </div>

      {value.preset === "custom" && (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-muted-foreground">{isSingleDay ? "Date:" : "From:"}</span>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1 h-8" data-testid={`${testIdPrefix}-start`}>
                <CalendarBlank size={14} /> {format(value.start, "MMM d, yyyy")}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={value.start}
                onSelect={(d) => d && set({ start: d })}
                initialFocus
              />
            </PopoverContent>
          </Popover>
          {!isSingleDay && (
            <>
              <span className="text-muted-foreground">To:</span>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-1 h-8" data-testid={`${testIdPrefix}-end`}>
                    <CalendarBlank size={14} /> {format(addDays(value.end, -1), "MMM d, yyyy")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={addDays(value.end, -1)}
                    onSelect={(d) => d && set({ end: addDays(d, 1) })}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-muted-foreground">Time of day:</span>
        <Input
          type="time"
          value={value.startTime || ""}
          onChange={(e) => set({ startTime: e.target.value })}
          className="h-8 w-32 font-mono"
          data-testid={`${testIdPrefix}-time-start`}
        />
        <span className="text-muted-foreground">to</span>
        <Input
          type="time"
          value={value.endTime || ""}
          onChange={(e) => set({ endTime: e.target.value })}
          className="h-8 w-32 font-mono"
          data-testid={`${testIdPrefix}-time-end`}
        />
        {(value.startTime || value.endTime) && (
          <Button
            variant="ghost"
            size="sm"
            className="h-8 text-xs"
            onClick={() => set({ startTime: "", endTime: "" })}
            data-testid={`${testIdPrefix}-time-clear`}
          >
            Clear
          </Button>
        )}
      </div>

      {!isSingleDay && (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-muted-foreground">Days:</span>
          {WEEKDAYS.map((label, idx) => {
            const active = !allDays && (value.weekdays || []).includes(idx);
            return (
              <button
                key={label}
                type="button"
                onClick={() => toggleDay(idx)}
                className={`px-2 py-1 text-xs rounded-md border transition-colors ${
                  active
                    ? "bg-primary text-primary-foreground border-primary"
                    : "border-border bg-card/50 text-muted-foreground"
                }`}
                data-testid={`${testIdPrefix}-day-${idx}`}
              >
                {label}
              </button>
            );
          })}
          {!allDays && (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 text-xs"
              onClick={() => set({ weekdays: [] })}
              data-testid={`${testIdPrefix}-day-clear`}
            >
              All days
            </Button>
          )}
        </div>
      )}
      {!isSingleDay && (
        <div className="flex flex-wrap items-center gap-2 text-sm pt-1 border-t border-border/60">
          <span className="text-muted-foreground">Compare:</span>
          <button
            type="button"
            onClick={() => set({ comparePrev: !value.comparePrev })}
            className={`px-2.5 py-1 text-xs rounded-md border transition-colors ${
              value.comparePrev
                ? "bg-primary text-primary-foreground border-primary"
                : "border-border bg-card/50 text-muted-foreground hover:border-muted-foreground/40"
            }`}
            data-testid={`${testIdPrefix}-compare-prev`}
            title="Show delta vs the equivalent previous window of the same length"
          >
            {value.comparePrev ? "✓ vs previous period" : "vs previous period"}
          </button>
        </div>
      )}
    </div>
  );
};
